import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { likeTrack, unlikeTrack } from '../redux/thunk/likesThunk';
import { entityId } from '../utils/entityId';
import PillFace from './PillFace';

// Reliable like-state matching is by track id. The text key (artist|||title)
// drifts between data sources (e.g. a mix tracklist vs. how the track was
// originally liked), which left genuinely-liked tracks showing an empty heart.
// Build a memoized id-set from the user's liked tracks (each carries
// track_public_id); rebuilds only when the likes list reference changes.
let _likedRef = null;
let _likedIdSet = new Set();
const likedIdsOf = (likedTracks) => {
    if (likedTracks === _likedRef) return _likedIdSet;
    _likedRef = likedTracks;
    _likedIdSet = new Set((likedTracks || [])
        .map((t) => entityId(t))
        .filter(Boolean));
    return _likedIdSet;
};

const LikeButton = React.forwardRef(({ track, prominent = false }, ref) => {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const likedSet = useSelector(state => state.likes.likedSet);
    const likedTracks = useSelector(state => state.likes.likedTracks);
    // .pop = confirmation beat on a user-initiated like (prominent-pill CSS).
    const [pop, setPop] = useState(false);

    const key = track.artist && track.title ? `${track.artist}|||${track.title}` : null;
    const pid = entityId(track);
    // Match by id first (robust), fall back to the text key.
    const isLiked = (pid && likedIdsOf(likedTracks).has(pid)) || (key ? !!likedSet[key] : false);

    const handleClick = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isAuthenticated || !key) return;
        try {
            if (isLiked) {
                setPop(false);
                dispatch(unlikeTrack({ artist: track.artist, title: track.title }));
            } else {
                setPop(true);
                dispatch(likeTrack(track));
            }
        } catch (err) {
            console.error('Like action failed:', err);
        }
    }, [dispatch, isAuthenticated, isLiked, key, track]);

    if (!isAuthenticated || !track.artist) return null;

    const heart = (
        <svg width="13" height="13" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
    );

    return (
        <button
            type="button"
            ref={ref}
            className={`track-like-btn${prominent ? ' prominent' : ''}${isLiked ? ' liked' : ''}${pop ? ' pop' : ''}`}
            title={isLiked ? 'Unlike' : 'Like'}
            onClick={handleClick}
            onTouchEnd={(e) => { e.stopPropagation(); }}
            onAnimationEnd={(e) => { if (e.animationName === 'pill-pop') setPop(false); }}
        >
            {prominent ? <PillFace icon={heart} label={isLiked ? 'Liked' : 'Like'} longest="Liked" /> : heart}
        </button>
    );
});

export default LikeButton;
